import type {
  AdminVehicleCatalogCreateGenerationBody,
  AdminVehicleCatalogPatchGenerationBody,
  AdminVehicleGenerationDto,
  AdminVehicleGenerationListDto,
} from '@carcheck/shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { ADMIN_VEHICLE_CATALOG_KEY } from './admin-vehicle-catalog';
import { apiClient } from './client';

/**
 * Generations of one catalogue model (AUT-450 / VG-03), against
 * `GET|POST /admin/vehicle-catalog/models/:id/generations` and
 * `PATCH /admin/vehicle-catalog/generations/:id`.
 *
 * The list key lives under the `['admin','vehicle-catalog']` prefix, so the
 * catalogue write runner (`refreshCatalog`) refetches it together with the
 * make detail — whose models carry `generationCount`. A generation write does
 * NOT move the model's `updatedAt`.
 *
 * `updatedAt` travels as the exact string the server sent. Writes pass
 * `skipAutoErrorToast`: the page maps the error `code` itself.
 */
const BASE = '/admin/vehicle-catalog';

export const catalogGenerationsKey = (modelId: string): readonly unknown[] => [
  ...ADMIN_VEHICLE_CATALOG_KEY,
  'generations',
  modelId,
];

/** Off while no model is open. Newest first, hidden rows included. */
export const useModelGenerations = (
  modelId: string | null,
): UseQueryResult<AdminVehicleGenerationListDto, Error> =>
  useQuery({
    queryKey: catalogGenerationsKey(modelId ?? ''),
    enabled: modelId !== null,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminVehicleGenerationListDto>(
        `${BASE}/models/${modelId ?? ''}/generations`,
        { signal },
      );
      return data;
    },
  });

const quiet = { skipAutoErrorToast: true } as const;

export const createCatalogGeneration = async (
  modelId: string,
  body: AdminVehicleCatalogCreateGenerationBody,
): Promise<AdminVehicleGenerationDto> =>
  (
    await apiClient.post<AdminVehicleGenerationDto>(
      `${BASE}/models/${modelId}/generations`,
      body,
      quiet,
    )
  ).data;

export const patchCatalogGeneration = async (
  id: string,
  body: AdminVehicleCatalogPatchGenerationBody,
): Promise<AdminVehicleGenerationDto> =>
  (await apiClient.patch<AdminVehicleGenerationDto>(`${BASE}/generations/${id}`, body, quiet)).data;
